import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Crown, Trash2, UserPlus, Search, ShieldCheck, Activity, Users as UsersIcon } from 'lucide-react';

const SCOPE_OPTIONS = [
  { value: 'approve_listings', label: 'Approve Properties' },
  { value: 'approve_projects', label: 'Approve Projects' },
  { value: 'approve_plots', label: 'Approve Plots' },
  { value: 'approve_agents', label: 'Approve Agents' },
  { value: 'approve_agencies', label: 'Approve Agencies' },
  { value: 'approve_developers', label: 'Approve Developers' },
  { value: 'moderate_reviews', label: 'Moderate Reviews' },
  { value: 'handle_reports', label: 'Handle Reports' },
  { value: 'manage_featured', label: 'Manage Featured' },
  { value: 'manage_campaigns', label: 'Manage Campaigns' },
];

const PERM_FLAGS = [
  { key: 'can_create_tasks', label: 'Create tasks' },
  { key: 'can_assign_tasks', label: 'Assign tasks' },
  { key: 'can_manage_team_members', label: 'Manage team members' },
  { key: 'can_view_all_team_data', label: 'View all team data' },
  { key: 'can_view_department_analytics', label: 'View department analytics' },
  { key: 'can_manage_prospects', label: 'Manage prospects' },
  { key: 'can_manage_campaigns', label: 'Manage campaigns' },
] as const;

interface TeamManageDrawerProps {
  team: any | null;
  department: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamManageDrawer({ team, department, open, onOpenChange }: TeamManageDrawerProps) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [permFlags, setPermFlags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (team) setScope(team.scope || []);
  }, [team]);

  // Members
  const { data: members = [] } = useQuery({
    queryKey: ['team-members', team?.id],
    queryFn: async () => {
      if (!team) return [];
      const { data, error } = await supabase
        .from('internal_team_members')
        .select('*')
        .eq('team_id', team.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!team,
  });

  // Profiles for members + leader
  const { data: profiles = [] } = useQuery({
    queryKey: ['team-member-profiles', members, team?.team_leader_id],
    queryFn: async () => {
      const ids = [...new Set([...members.map((m: any) => m.user_id), team?.team_leader_id].filter(Boolean))];
      if (!ids.length) return [];
      const { data, error } = await supabase.from('profiles').select('user_id, full_name, avatar_url').in('user_id', ids);
      if (error) throw error;
      return data || [];
    },
    enabled: members.length > 0 || !!team?.team_leader_id,
  });

  // Searchable users for "Add Member"
  const { data: searchResults = [] } = useQuery({
    queryKey: ['user-search-team', memberSearch, team?.id],
    queryFn: async () => {
      if (!memberSearch || memberSearch.length < 2) return [];
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .ilike('full_name', `%${memberSearch}%`)
        .limit(8);
      if (error) throw error;
      const existingIds = new Set(members.map((m: any) => m.user_id));
      return (data || []).filter((p: any) => !existingIds.has(p.user_id));
    },
    enabled: memberSearch.length >= 2,
  });

  // Workload (last 30d) — admin actions performed by team members
  const { data: workload } = useQuery({
    queryKey: ['team-workload', team?.id, members],
    queryFn: async () => {
      const ids = members.map((m: any) => m.user_id);
      if (!ids.length) return { actions: 0, approvals: 0, rejections: 0 };
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('admin_actions')
        .select('action')
        .in('admin_id', ids)
        .gte('created_at', since);
      const actions = data || [];
      return {
        actions: actions.length,
        approvals: actions.filter((a: any) => a.action?.toLowerCase().includes('approve')).length,
        rejections: actions.filter((a: any) => a.action?.toLowerCase().includes('reject')).length,
      };
    },
    enabled: !!team && members.length > 0,
  });

  const getProfile = (userId: string) => profiles.find((p: any) => p.user_id === userId);

  // Mutations
  const saveScope = useMutation({
    mutationFn: async (newScope: string[]) => {
      const { error } = await supabase.from('internal_teams').update({ scope: newScope }).eq('id', team.id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['internal-teams-all'] }); toast.success('Scope updated'); },
    onError: (e: any) => toast.error(e.message),
  });

  const addMember = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.from('internal_team_members').insert({ team_id: team.id, user_id: userId });
      if (error && !error.message.includes('duplicate')) throw error;
      // Auto-assign department default role
      if (department?.default_role) {
        await supabase.from('user_roles').insert({ user_id: userId, role: department.default_role as any });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members', team?.id] });
      qc.invalidateQueries({ queryKey: ['internal-teams-all'] });
      setMemberSearch('');
      toast.success('Member added');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      const { error } = await supabase.from('internal_team_members').delete().eq('id', memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members', team?.id] });
      qc.invalidateQueries({ queryKey: ['internal-teams-all'] });
      toast.success('Member removed');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setLeader = useMutation({
    mutationFn: async (userId: string | null) => {
      const { error } = await supabase.from('internal_teams').update({ team_leader_id: userId }).eq('id', team.id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['internal-teams-all'] }); toast.success('Team leader updated'); },
    onError: (e: any) => toast.error(e.message),
  });

  const applyPermissions = useMutation({
    mutationFn: async () => {
      if (!members.length || !department) return;
      const rows = members.map((m: any) => ({
        user_id: m.user_id,
        department_id: department.id,
        ...permFlags,
      }));
      const { error } = await supabase
        .from('internal_staff_permissions')
        .upsert(rows, { onConflict: 'user_id,department_id' as any });
      if (error) throw error;
    },
    onSuccess: () => toast.success('Permissions applied to all team members'),
    onError: (e: any) => toast.error(e.message),
  });

  if (!team) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-primary" /> {team.name}
          </SheetTitle>
          <SheetDescription>
            {department?.name} · {members.length} members
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="members" className="mt-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="members">Members</TabsTrigger>
            <TabsTrigger value="scope">Scope</TabsTrigger>
            <TabsTrigger value="permissions">Permissions</TabsTrigger>
            <TabsTrigger value="workload">Workload</TabsTrigger>
          </TabsList>

          {/* MEMBERS */}
          <TabsContent value="members" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Add Member</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users by name…"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="border rounded-lg divide-y max-h-48 overflow-auto">
                  {searchResults.map((p: any) => (
                    <div key={p.user_id} className="flex items-center justify-between p-2 hover:bg-muted/50">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-xs font-bold">
                          {(p.full_name || '?')[0]?.toUpperCase()}
                        </div>
                        {p.full_name || 'Unnamed'}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => addMember.mutate(p.user_id)} disabled={addMember.isPending}>
                        <UserPlus className="h-3.5 w-3.5 mr-1" /> Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {department?.default_role && (
                <p className="text-xs text-muted-foreground">
                  New members are auto-assigned the <Badge variant="secondary" className="text-[10px]">{department.default_role}</Badge> role.
                </p>
              )}
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>Current Members</Label>
              {members.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No members yet</p>
              ) : (
                <div className="space-y-1">
                  {members.map((m: any) => {
                    const p = getProfile(m.user_id);
                    const isLeader = team.team_leader_id === m.user_id;
                    return (
                      <div key={m.id} className="flex items-center justify-between p-2 rounded-lg border bg-card">
                        <div className="flex items-center gap-2 text-sm">
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">
                            {(p?.full_name || '?')[0]?.toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium flex items-center gap-1">
                              {p?.full_name || 'Unknown'}
                              {isLeader && <Crown className="h-3.5 w-3.5 text-yellow-500" />}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              Joined {new Date(m.joined_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {isLeader ? (
                            <Button size="sm" variant="ghost" onClick={() => setLeader.mutate(null)} title="Demote">
                              <Crown className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => setLeader.mutate(m.user_id)} title="Promote to Leader">
                              <Crown className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeMember.mutate(m.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* SCOPE */}
          <TabsContent value="scope" className="space-y-4 mt-4">
            <div>
              <Label className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Governance Scope</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Tick which platform-governance powers this team has. Members inherit these.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {SCOPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer hover:bg-muted/50">
                  <Checkbox
                    checked={scope.includes(opt.value)}
                    onCheckedChange={(checked) => {
                      setScope((s) => checked ? [...s, opt.value] : s.filter((v) => v !== opt.value));
                    }}
                  />
                  <span className="text-sm">{opt.label}</span>
                </label>
              ))}
            </div>
            <Button onClick={() => saveScope.mutate(scope)} disabled={saveScope.isPending} className="w-full">
              Save Scope
            </Button>
          </TabsContent>

          {/* PERMISSIONS (bulk per-member) */}
          <TabsContent value="permissions" className="space-y-4 mt-4">
            <p className="text-xs text-muted-foreground">
              Apply these permission overrides to <strong>all {members.length} members</strong> of this team.
            </p>
            <div className="space-y-2">
              {PERM_FLAGS.map((f) => (
                <div key={f.key} className="flex items-center justify-between p-2 rounded-lg border">
                  <Label htmlFor={f.key} className="text-sm cursor-pointer">{f.label}</Label>
                  <Switch
                    id={f.key}
                    checked={!!permFlags[f.key]}
                    onCheckedChange={(c) => setPermFlags((p) => ({ ...p, [f.key]: c }))}
                  />
                </div>
              ))}
            </div>
            <Button onClick={() => applyPermissions.mutate()} disabled={applyPermissions.isPending || !members.length} className="w-full">
              Apply to All Members
            </Button>
          </TabsContent>

          {/* WORKLOAD */}
          <TabsContent value="workload" className="space-y-4 mt-4">
            <Label className="flex items-center gap-2"><Activity className="h-4 w-4" /> Last 30 Days</Label>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 rounded-lg border bg-card text-center">
                <div className="text-2xl font-bold">{workload?.actions ?? 0}</div>
                <div className="text-xs text-muted-foreground">Total actions</div>
              </div>
              <div className="p-4 rounded-lg border bg-card text-center">
                <div className="text-2xl font-bold text-green-600">{workload?.approvals ?? 0}</div>
                <div className="text-xs text-muted-foreground">Approvals</div>
              </div>
              <div className="p-4 rounded-lg border bg-card text-center">
                <div className="text-2xl font-bold text-red-600">{workload?.rejections ?? 0}</div>
                <div className="text-xs text-muted-foreground">Rejections</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Counts moderation/governance actions performed by team members across approvals, audits, and reports.
            </p>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
